import React, { useState } from "react";
import styled from "styled-components";
import { FaTemperatureHigh } from "react-icons/fa6";
import { LuWeight } from "react-icons/lu";
import { GiFilmSpool } from "react-icons/gi";
import { CiEdit } from "react-icons/ci";
import InputField from "./InputField.tsx";
import { IoCloseOutline, IoTrash } from "react-icons/io5";
import { SpoolSVG } from "../../../assets/SpoolSVG.tsx";
import { BsCreditCard2Front } from "react-icons/bs";

import { LuPrinter } from "react-icons/lu";
import PrinterLabel from "./PrinterLabel.tsx";

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  max-width: 250px;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  margin-top: 1.5rem;
`;

const Spool = styled.div<{ color?: string }>`
  position: absolute;
  width: 85px;
  height: 85px;
  border-radius: 50%;
  // background-color: ${({ color }) => color || "blue"};
  top: -35px; /* Moves spool slightly outside the box */
  left: 50%;
  transform: translateX(-50%);
`;

const ColorName = styled.div`
  color: var(--secondary-font-color);
  font-weight: 500;
  font-size: 14px;
  text-transform: uppercase;
  margin-top: 110px; /* Pushes text down so it doesn't overlap */
`;

const ColorCode = styled.div`
  color: var(--tertiary-font-color);
  padding-top: 4px;
  padding-bottom: 4px;
  font-size: 12px;
`;

const SettingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(0, 0, 0, 0.15);
  padding-top: 10px;
  gap: 1rem;
`;
const SettingRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 4px;
`;
const SettingItem = styled.div`
  align-items: center;
  display: flex;
  gap: 5px;
  flex: 1;
`;

const EditButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  background-color: transparent;
  font-size: 1.5rem;
  color: var(--tertiary-font-color);
  border: none;
  outline: none;
`;

const PrintButton = styled.button`
  position: absolute;
  top: 12px;
  right: 50px;
  cursor: pointer;
  background-color: transparent;
  font-size: 1.2rem;
  color: var(--tertiary-font-color);
  border: none;
  outline: none;
`;

const SaveButton = styled.button`
  cursor: pointer;
  background-color: transparent;
  font-size: 1.5rem;

  border: none;
  outline: none;
`;

const CloseButton = styled.button`
  cursor: pointer;
  background-color: transparent;
  font-size: 1.5rem;

  border: none;
  outline: none;
`;

const EditContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding-top: 1rem;
`;

export type FilamentConfig = {
  material: string;
  supplierName: string;
  spoolWeight: number;
  weight: number;
  temperature: number;
  filamentID: number;
};

interface Props {
  colorName?: string;
  colorCode: string;
  isAdminMode?: boolean;
  filamentConfig: FilamentConfig;
  onWeightUpdated: (filamentID: number, updatedWeight: number) => void;
  readonly?: boolean;
  onDelete?: (filamentID: number) => void;
  printLabel?: (name, type, id) => void;
}

const FilamentTile: React.FC<Props> = ({
  printLabel,
  colorName,
  colorCode,
  isAdminMode,
  filamentConfig,
  onWeightUpdated,
  readonly,
  onDelete,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [weight, setWeight] = useState(filamentConfig?.weight.toString() || "");

  const handleSave = async () => {
    if (filamentConfig && weight) {
      try {
        await onWeightUpdated(filamentConfig?.filamentID, parseFloat(weight));

        setIsEditMode(false);
      } catch (error) {
        console.error("Error updating filament weight:", error);
      }
    }
  };

  return (
    <Wrapper
      data-testid="filament-tile"
      id={`${filamentConfig?.filamentID}` || colorCode}
    >
      <Spool>
        {" "}
        <SpoolSVG color={colorCode} />
      </Spool>
      <ColorName>{colorName}</ColorName>

      <>
        {isAdminMode && (
          <ColorCode>
            {colorCode} {"[ id:"} {filamentConfig?.filamentID} {"]"}
          </ColorCode>
        )}
        <SettingWrapper>
          <SettingRow>
            <SettingItem>
              <GiFilmSpool />
              {filamentConfig?.material}
            </SettingItem>
            <SettingItem>
              <BsCreditCard2Front /> {filamentConfig?.supplierName}
            </SettingItem>
          </SettingRow>

          {isAdminMode && (
            <SettingRow>
              <SettingItem>
                <LuWeight /> {filamentConfig?.weight}g
              </SettingItem>
              <SettingItem>
                <FaTemperatureHigh /> {filamentConfig?.temperature}°C
              </SettingItem>
            </SettingRow>
          )}
        </SettingWrapper>

        {!isEditMode && isAdminMode && !readonly && (
          <EditButton
            onClick={() => {
              setIsEditMode(true);
            }}
          >
            <CiEdit />
          </EditButton>
        )}

        {isAdminMode && !readonly && printLabel !== undefined && (
          <PrintButton
            id="print-button"
            onClick={() => {
              printLabel &&
                printLabel(
                  colorName,
                  filamentConfig?.material,
                  filamentConfig?.filamentID
                );
            }}
          >
            <LuPrinter />
          </PrintButton>
        )}

        {!isEditMode && isAdminMode && readonly && onDelete && (
          <EditButton
            onClick={() => {
              const confirmDelete = window.confirm(
                "Ești sigur că vrei să filamentul? Acaesta acțiune va sterge doar compatibilitatea filamentului cu imprimanta selectata!"
              );
              if (confirmDelete) {
                onDelete && onDelete(filamentConfig?.filamentID);
              }
            }}
          >
            <IoTrash />
          </EditButton>
        )}

        {isEditMode && isAdminMode && (
          <EditContainer>
            <InputField
              name="weight"
              type="number"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              isRequired
              placeholder="200"
              icon={<LuWeight />}
            />
            <div style={{ display: "flex", flexDirection: "row" }}>
              <CloseButton
                onClick={() => {
                  setIsEditMode(false);
                }}
              >
                <IoCloseOutline />
              </CloseButton>
              <SaveButton onClick={handleSave}>
                <CiEdit />
              </SaveButton>
            </div>
          </EditContainer>
        )}
      </>
    </Wrapper>
  );
};

export default FilamentTile;
